import { useMemo, useState } from "react";
import { ChevronRight, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Grafico de rosca interativo em SVG PURO, seguindo o prompt do dono:
 * cada fatia e' um <circle> completo virando arco por dasharray/dashoffset,
 * legenda e grafico sao O MESMO controle (hover espelhado), miolo com total e
 * hover, paleta fixa de 7 cores que NUNCA cicla — da 8a categoria em diante
 * tudo vira uma fatia "resto" expansivel na legenda.
 */
const R = 64, ESPESSURA = 26, CX = 90, CY = 90;
const C = 2 * Math.PI * R;
const FOLGA = 3;
const PALETA = ["#4F46E5", "#F59E0B", "#10B981", "#EC4899", "#0EA5E9", "#EF4444", "#84CC16"];
const COR_RESTO = "#94A3B8";

export default function GraficoRosca({ itens, rotuloTotal, rotuloResto = "Outros", aoSelecionar,
                                       centroValor = null, centroRotulo = null }) {
  const [hoverId, setHoverId] = useState(null);
  const [restoAberto, setRestoAberto] = useState(false);

  const { fatias, total, agregados } = useMemo(() => {
    const validos = (itens || []).filter((i) => i.valor > 0).sort((a, b) => b.valor - a.valor);
    const principais = validos.slice(0, 7).map((i, idx) => ({ ...i, cor: PALETA[idx] }));
    const resto = validos.slice(7);
    const lista = [...principais];
    if (resto.length) {
      lista.push({
        id: "__resto__", nome: rotuloResto, cor: COR_RESTO,
        valor: resto.reduce((s, i) => s + i.valor, 0),
      });
    }
    const soma = lista.reduce((s, i) => s + i.valor, 0);
    let acumulado = 0;
    const folga = lista.length === 1 ? 0 : FOLGA;
    const comArco = lista.map((i) => {
      const comprimento = (i.valor / soma) * C;
      const arco = {
        ...i,
        dash: `${Math.max(comprimento - folga, 0.5)} ${C - comprimento + folga}`,
        offset: -acumulado,
      };
      acumulado += comprimento;
      return arco;
    });
    return { fatias: comArco, total: soma, agregados: resto };
  }, [itens, rotuloResto]);

  if (!total) {
    return (
      <p className="text-sm text-muted-foreground py-10 text-center">
        Sem dados no período — o gráfico mostra a distribuição de {rotuloTotal} quando existirem.
      </p>
    );
  }

  const emHover = fatias.find((f) => f.id === hoverId);
  const pctDe = (v) => Math.round((v / total) * 100);
  const clicar = (f) => {
    if (f.id === "__resto__") { setRestoAberto((v) => !v); return; }
    aoSelecionar?.(f);
  };

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <div className="relative w-[240px] h-[240px] sm:w-[192px] sm:h-[192px] shrink-0">
        <svg viewBox="0 0 180 180" className="w-full h-full" role="img"
          aria-label={`Distribuição de ${rotuloTotal}: ${fatias.map((f) => `${f.nome} ${pctDe(f.valor)}%`).join(", ")}`}>
          <g transform={`rotate(-90 ${CX} ${CY})`}>
            {fatias.map((f) => (
              <circle key={f.id} cx={CX} cy={CY} r={R} fill="none"
                stroke={f.cor}
                strokeWidth={hoverId === f.id ? ESPESSURA + 6 : ESPESSURA}
                strokeDasharray={f.dash} strokeDashoffset={f.offset}
                opacity={hoverId && hoverId !== f.id ? 0.35 : 1}
                onMouseEnter={() => setHoverId(f.id)} onMouseLeave={() => setHoverId(null)}
                onClick={() => clicar(f)}
                className={aoSelecionar || f.id === "__resto__" ? "cursor-pointer" : ""}
                style={{ transition: "opacity 150ms, stroke-width 150ms" }} />
            ))}
          </g>
        </svg>
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center max-w-[96px]">
            {emHover ? (
              <>
                <p className="text-xs font-medium truncate">{emHover.nome}</p>
                <p className="text-xl font-bold tabular-nums">{emHover.valor.toLocaleString("pt-BR")}</p>
                <p className="text-[10px] text-muted-foreground tabular-nums">{pctDe(emHover.valor)}%</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold tabular-nums">
                  {centroValor ?? total.toLocaleString("pt-BR")}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {centroRotulo ?? rotuloTotal}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="w-full sm:flex-1 max-h-56 overflow-y-auto space-y-0.5">
        {fatias.map((f) => (
          <div key={f.id}>
            <button onClick={() => clicar(f)}
              onMouseEnter={() => setHoverId(f.id)} onMouseLeave={() => setHoverId(null)}
              className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-left transition-colors ${
                hoverId === f.id ? "bg-muted/60" : ""}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: f.cor }} />
              {f.avatarUrl && (
                <img src={f.avatarUrl} alt="" className="w-6 h-6 rounded-full shrink-0" />
              )}
              <span className="flex-1 truncate text-muted-foreground">{f.nome}</span>
              <span className="font-bold tabular-nums">{f.valor.toLocaleString("pt-BR")}</span>
              <span className="w-9 text-right text-xs text-muted-foreground tabular-nums">
                {pctDe(f.valor)}%
              </span>
              {f.id === "__resto__"
                ? (restoAberto ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                               : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />)
                : aoSelecionar && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
            </button>
            {f.id === "__resto__" && restoAberto && (
              <div className="ml-4 border-l pl-2 space-y-0.5">
                {agregados.map((i) => (
                  <button key={i.id} onClick={() => aoSelecionar?.(i)}
                    className="w-full flex items-center gap-2 rounded-lg px-2 py-1 text-xs text-left hover:bg-muted/60 transition-colors">
                    <span className="flex-1 truncate text-muted-foreground">{i.nome}</span>
                    <span className="font-semibold tabular-nums">{i.valor.toLocaleString("pt-BR")}</span>
                    <span className="w-9 text-right text-muted-foreground tabular-nums">{pctDe(i.valor)}%</span>
                    {aoSelecionar && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
